import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { bairrosApi } from '@/lib/api/bairros';
import { adminApi } from '@/lib/api/admin';
import { useAuthStore } from '@/lib/stores/authStore';
import type { BairroView } from '@/lib/hooks/useBairros';
import { useEscala } from '@/lib/escala';
import { toast } from '@/lib/stores/toastStore';
import { Drawer } from '@/design-system/Drawer';
import { Info } from '@/design-system/Info';

/**
 * Identificador estável do território, derivado do nome.
 *
 * A API exige `^[a-z0-9-]+$`. Os acentos viram a letra base para que "Messejana"
 * e "Jangurussu" produzam um slug legível em vez de uma sequência de escapes.
 */
export function gerarSlug(nome: string): string {
  return nome
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

/** Campos editáveis, agrupados como aparecem no perfil do território. */
const CAMPOS: Array<{ chave: string; rotulo: string; grupo: string; max?: number }> = [
  { chave: 'populacao', rotulo: 'População', grupo: 'Demografia' },
  { chave: 'domicilios', rotulo: 'Domicílios', grupo: 'Demografia' },
  { chave: 'eleitores', rotulo: 'Eleitores', grupo: 'Demografia' },
  { chave: 'renda_media', rotulo: 'Renda média (R$)', grupo: 'Demografia' },

  { chave: 'votos', rotulo: 'Votos do mandato', grupo: 'Desempenho eleitoral' },
  { chave: 'percentual', rotulo: 'Percentual (%)', grupo: 'Desempenho eleitoral', max: 100 },
  { chave: 'cobertura_cadastro', rotulo: 'Cobertura de cadastro (%)', grupo: 'Desempenho eleitoral', max: 100 },

  { chave: 'ubs', rotulo: 'UBS existentes', grupo: 'Indicadores de serviço' },
  { chave: 'ubs_ideal', rotulo: 'UBS ideais', grupo: 'Indicadores de serviço' },
  { chave: 'fila_dias', rotulo: 'Fila de consulta (dias)', grupo: 'Indicadores de serviço' },
  { chave: 'fila_creche', rotulo: 'Fila de creche (vagas)', grupo: 'Indicadores de serviço' },
  { chave: 'pavimentacao_pct', rotulo: 'Pavimentação (%)', grupo: 'Indicadores de serviço', max: 100 },
  { chave: 'esgoto_pct', rotulo: 'Esgoto tratado (%)', grupo: 'Indicadores de serviço', max: 100 },
  { chave: 'pontos_escuros', rotulo: 'Pontos escuros', grupo: 'Indicadores de serviço' },
  { chave: 'pracas', rotulo: 'Praças', grupo: 'Indicadores de serviço' },
  { chave: 'necessidade', rotulo: 'Índice de necessidade (0-100)', grupo: 'Indicadores de serviço', max: 100 },
];

const GRUPOS = ['Demografia', 'Desempenho eleitoral', 'Indicadores de serviço'];

/** Mapeia o formato curto usado nas telas de volta para o contrato da API. */
const VALOR_ATUAL: Record<string, keyof BairroView> = {
  populacao: 'pop',
  domicilios: 'dom',
  eleitores: 'eleit',
  renda_media: 'renda',
  votos: 'votos',
  percentual: 'pct',
  cobertura_cadastro: 'cad',
  ubs: 'ubs',
  ubs_ideal: 'ubsIdeal',
  fila_dias: 'fila',
  fila_creche: 'creche',
  pavimentacao_pct: 'pav',
  esgoto_pct: 'esgoto',
  pontos_escuros: 'escuros',
  pracas: 'pracas',
  necessidade: 'nec',
};

/**
 * Cadastro e edição de território.
 *
 * `bairro` ausente significa criação. O formulário de indicadores é o mesmo nos
 * dois casos — são os mesmos campos, e manter duas telas só geraria divergência.
 */
export function ModalTerritorio({
  bairro,
  onFechar,
}: {
  bairro?: BairroView | null;
  onFechar: () => void;
}) {
  const token = useAuthStore((s) => s.token) || '';
  const queryClient = useQueryClient();
  const { unidade } = useEscala();
  const criando = !bairro;

  // Município e UF vêm do mandato. A coluna tem default 'Fortaleza'/'CE' no
  // banco, então criar sem informar colocaria todo território em Fortaleza.
  const { data: mandato } = useQuery({
    queryKey: ['mandato'],
    queryFn: () => adminApi.getMandato(token),
    enabled: !!token,
    staleTime: 60_000,
  });

  const [nome, setNome] = useState(bairro?.n ?? '');
  const [slug, setSlug] = useState('');
  const [slugTocado, setSlugTocado] = useState(false);
  const [valores, setValores] = useState<Record<string, number>>(() =>
    Object.fromEntries(
      CAMPOS.map((c) => [c.chave, bairro ? Number(bairro[VALOR_ATUAL[c.chave]!] ?? 0) : 0]),
    ),
  );

  // Enquanto o usuário não editar o identificador, ele acompanha o nome.
  const slugEfetivo = slugTocado ? slug : gerarSlug(nome);

  const aoTrocarNome = (valor: string) => {
    setNome(valor);
    if (!slugTocado) setSlug(gerarSlug(valor));
  };

  const erroDe = (err: any, padrao: string) => {
    const msg = err?.response?.data?.message;
    return Array.isArray(msg) ? msg.join(' · ') : msg || padrao;
  };

  const salvar = useMutation({
    mutationFn: (dados: any) =>
      criando ? bairrosApi.create(dados, token) : bairrosApi.update(bairro!.bairroId, dados, token),
    onSuccess: () => {
      // Território alimenta metas, matriz e painel; todos precisam recarregar.
      queryClient.invalidateQueries({ queryKey: ['bairros'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['metas'] });
      queryClient.invalidateQueries({ queryKey: ['matriz'] });
      toast(criando ? `${unidade} cadastrado.` : 'Território atualizado.');
      onFechar();
    },
    // A API recusa identificador repetido com 409 e mensagem própria.
    onError: (err: any) =>
      toast(
        erroDe(
          err,
          criando ? 'Erro ao cadastrar o território.' : 'Erro ao atualizar o território.',
        ),
      ),
  });

  const submeter = (e: React.FormEvent) => {
    e.preventDefault();
    if (!nome.trim()) {
      toast('O nome do território é obrigatório.');
      return;
    }
    if (criando && !/^[a-z0-9-]+$/.test(slugEfetivo)) {
      toast('O identificador deve conter apenas letras minúsculas, números e hífen.');
      return;
    }

    salvar.mutate(
      criando
        ? {
            nome: nome.trim(),
            slug: slugEfetivo,
            uf: mandato?.uf,
            municipio: mandato?.cidade,
            ...valores,
          }
        : { nome: nome.trim(), ...valores },
    );
  };

  return (
    <form onSubmit={submeter}>
      <Drawer
        aberto
        titulo={criando ? `Novo ${unidade.toLowerCase()}` : `Editar território · ${bairro!.n}`}
        aoFechar={onFechar}
        rodape={
          <>
            <button type="button" className="btn" onClick={onFechar}>
              Cancelar
            </button>
            <button type="submit" className="btn pri" disabled={salvar.isPending}>
              {salvar.isPending ? 'Salvando...' : criando ? 'Cadastrar território' : 'Salvar território'}
            </button>
          </>
        }
      >
        <div className="field">
          <label>Nome do território *</label>
          <input
            className="in"
            value={nome}
            onChange={(e) => aoTrocarNome(e.target.value)}
            placeholder={criando ? `Ex.: nome do ${unidade.toLowerCase()}` : undefined}
            autoFocus={criando}
            required
          />
        </div>

        {criando && (
          <div className="field">
            <label>Identificador</label>
            <input
              className="in mono"
              value={slugEfetivo}
              onChange={(e) => {
                setSlugTocado(true);
                setSlug(e.target.value);
              }}
            />
            <div style={{ fontSize: 11, color: 'var(--ink3)', marginTop: 4 }}>
              Usado internamente e nos links. Gerado a partir do nome; só mude se precisar.
              {mandato ? ` Município: ${mandato.cidade} / ${mandato.uf}.` : ''}
            </div>
          </div>
        )}

        {GRUPOS.map((grupo) => (
          <div key={grupo}>
            <h3 style={{ fontSize: 13, margin: '6px 0 8px' }}>{grupo}</h3>
            <div className="grid g2">
              {CAMPOS.filter((c) => c.grupo === grupo).map((c) => (
                <div className="field" key={c.chave}>
                  <label style={{ fontSize: 11 }}>{c.rotulo}</label>
                  <input
                    type="number"
                    min={0}
                    max={c.max}
                    step={c.chave === 'percentual' ? '0.1' : '1'}
                    className="in num"
                    value={valores[c.chave] ?? 0}
                    onChange={(e) =>
                      setValores({ ...valores, [c.chave]: Number(e.target.value) || 0 })
                    }
                  />
                </div>
              ))}
            </div>
          </div>
        ))}

        <div className="note" style={{ fontSize: 12 }}>
          {criando ? 'Os indicadores podem ficar em zero agora e ser preenchidos depois. ' : ''}
          Alterar o índice de necessidade muda o que a plataforma recomenda.{' '}
          <Info titulo="O que depende do índice de necessidade">
            Ele alimenta as prioridades do painel, os alertas e a matriz território × tema.
          </Info>
        </div>
      </Drawer>
    </form>
  );
}
